import CustomToast from "../../utils/functions/CustomToast";
import HttpClient from "../../utils/functions/HttpClient";
import ServerEndpoints from "../../utils/constants/ServerEndpoints";
import Errors from "../../utils/constants/Errors";

const request = async (operation, { setApiError, setIsLoading } = {}) => {
  setApiError?.(false);

  try {
    return await operation();
  } catch (err) {
    setApiError?.(true);
    handleReservationRequestError(err);
    throw err;
  } finally {
    setIsLoading?.(false);
  }
};

const normalizeReservationQueryParams = (params = {}) => {
  const normalizedParams = { ...params };

  if (normalizedParams.pageSize != null && normalizedParams.size == null) {
    normalizedParams.size = normalizedParams.pageSize;
  }

  delete normalizedParams.pageSize;

  return normalizedParams;
};

const mapRequestedAddOnsForApi = (requestedAddOns = []) =>
  requestedAddOns
    .filter((item) => item?.addOn?.id != null && Number(item?.quantity) > 0)
    .map((item) => ({
      id: item.id ?? null,
      addOn: item.addOn,
      quantity: Number(item.quantity),
    }));

const normalizeDiscountForApi = (discount) => {
  if (!discount) {
    return null;
  }

  const type = typeof discount.type === "string" ? discount.type.trim() : discount.type;

  if (!type) {
    return null;
  }

  return {
    ...discount,
    type,
    amount: Number(discount.amount || 0),
    percentage: Number(discount.percentage || 0),
  };
};

const buildReservationRequestPayload = (reservation) => ({
  startingDateTime: reservation.startingDateTime,
  endingDateTime: reservation.endingDateTime,
  effectiveEndingDateTime: reservation.effectiveEndingDateTime || null,
  eventType: reservation.eventType,
  numberOfSeats: Number(reservation.numberOfSeats || 0),
  addOns: mapRequestedAddOnsForApi(reservation.addOns),
  addOnsTotalCost: Number(reservation.addOnsTotalCost || 0),
  status: reservation.status,
  fullPackage: Boolean(reservation.fullPackage),
  securityDepositRefunded: Boolean(reservation.securityDepositRefunded),
  taxRate: Number(reservation.taxRate || 0),
  totalPrice: Number(reservation.totalPrice || 0),
  discount: normalizeDiscountForApi(reservation.discount),
  pricingStrategy: reservation.pricingStrategy || "FULL_DAY",
  userId: reservation.userId,
});

export const getAllReservations = async (
  params,
  setReservationsData,
  setApiError,
  setIsLoading
) => {
  const normalizedParams = normalizeReservationQueryParams(params);
  const response = await request(
    () =>
      HttpClient.getAxiosClient().get(
        `${ServerEndpoints.ADMIN_RESERVATIONS_ENDPOINT}`,
        {
          params: normalizedParams,
        }
      ),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setReservationsData(response.data);
  }
};

export const getReservationById = async (
  setReservationData,
  id,
  setApiError,
  setIsLoading
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().get(`${ServerEndpoints.RESERVATIONS_ENDPOINT}/${id}`),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setReservationData(response.data);
  }
};

export const getReservationByUserId = async (
  setReservationData,
  id,
  setApiError,
  setIsLoading,
  params
) => {
  const normalizedParams = normalizeReservationQueryParams(params);
  const response = await request(
    () =>
      HttpClient.getAxiosClient().get(
        `${ServerEndpoints.USERS_ENDPOINT}/${id}${ServerEndpoints.RESERVATIONS_ENDPOINT}`,
        { params: normalizedParams }
      ),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    setReservationData(response.data);
  }
};

export const cancelReservation = async (
  id,
  setState,
  setApiError,
  setIsLoading
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().delete(
        `${ServerEndpoints.ADMIN_RESERVATIONS_ENDPOINT}/${id}`
      ),
    { setApiError, setIsLoading }
  );

  if (response.status === 204) {
    CustomToast("Reservation successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

export const cancelReservations = async (
  ids,
  setState,
  setApiError,
  setIsLoading
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().delete(
        `${ServerEndpoints.ADMIN_RESERVATIONS_ENDPOINT}`,
        {
          data: ids,
        }
      ),
    { setApiError, setIsLoading }
  );

  if (response.status === 204) {
    CustomToast("Reservations successfully deleted", "success");
    setState?.((prev) => !prev);
  }
};

export const updateReservation = async (
  reservation,
  setState,
  setApiError,
  setIsLoading
) => {
  const payload = buildReservationRequestPayload(reservation);
  const response = await request(
    () =>
      HttpClient.getAxiosClient().put(
        `${ServerEndpoints.ADMIN_RESERVATIONS_ENDPOINT}/${reservation.id}`,
        payload
      ),
    { setApiError, setIsLoading }
  );

  if (response.status === 200) {
    CustomToast("Reservation successfully updated", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

export const createReservation = async (
  reservation,
  setState,
  setApiError,
  setIsLoading
) => {
  const payload = buildReservationRequestPayload(reservation);
  const response = await request(
    () =>
      HttpClient.getAxiosClient().post(
        `${ServerEndpoints.ADMIN_RESERVATIONS_ENDPOINT}`,
        payload
      ),
    { setApiError, setIsLoading }
  );

  if (response.status === 201 || response.status === 200) {
    CustomToast("Reservation successfully created", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

export const updateReservationStatus = async (
  id,
  action,
  setState,
  setApiError,
  setIsLoading
) => {
  const response = await request(
    () =>
      HttpClient.getAxiosClient().put(
        `${ServerEndpoints.ADMIN_RESERVATIONS_ENDPOINT}/${id}/status`,
        null,
        { params: { action } }
      ),
    { setApiError, setIsLoading }
  );

  if (response.status === 200 || response.status === 204) {
    CustomToast("Reservation successfully updated", "success");
    setState?.((prev) => !prev);
  }

  return response.data;
};

const getErrorStatus = (err) => err?.response?.status;

const getBackendErrorMessage = (err) => {
  const data = err?.response?.data;

  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return data.errors[0];
  }

  return data?.message || null;
};

const handleReservationRequestError = (err) => {
  const backendMessage = getBackendErrorMessage(err);

  switch (getErrorStatus(err)) {
    case 400:
      CustomToast(backendMessage || "Please review your data and try again.", "error");
      return;
    case 401:
      CustomToast("Please sign in to continue.", "error");
      return;
    case 403:
      CustomToast(backendMessage || "You are not allowed to perform this action.", "error");
      return;
    case 404:
      CustomToast(backendMessage || "The reservation could not be found.", "error");
      return;
    case 409:
      CustomToast(backendMessage || "The reservation request conflicts with the current schedule.", "error");
      return;
    case 503:
      CustomToast(Errors.SERVICE_UNAVALAIBLE_ERROR, "error");
      return;
    default:
      showGenericRequestError(err);
  }
};

const showGenericRequestError = (err) => {
  if (err?.response == null) {
    CustomToast(Errors.API_ERROR, "error");
    return;
  }

  const backendMessage = getBackendErrorMessage(err);
  CustomToast(backendMessage || Errors.API_ERROR, "error");
};
